import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Star, Eye, EyeOff } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';

const ManageTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ customer_name: '', content: '', rating: 5, featured: false, is_published: true });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.getAllTestimonials();
      setTestimonials(res.data);
    } catch { toast.error('Failed to load testimonials'); }
  };

  const resetForm = () => {
    setForm({ customer_name: '', content: '', rating: 5, featured: false, is_published: true });
    setEditing(null);
  };

  const handleSave = async () => {
    if (!form.customer_name.trim() || !form.content.trim()) {
      toast.error('Name and content are required');
      return;
    }
    try {
      if (editing) {
        await api.updateTestimonial(editing, form);
        toast.success('Testimonial updated');
      } else {
        await api.createTestimonial(form);
        toast.success('Testimonial created');
      }
      resetForm();
      load();
    } catch { toast.error('Failed to save testimonial'); }
  };

  const handleEdit = (t) => {
    setEditing(t.id);
    setForm({ customer_name: t.customer_name, content: t.content, rating: t.rating, featured: t.featured, is_published: t.is_published });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await api.deleteTestimonial(id);
      toast.success('Testimonial deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const togglePublish = async (t) => {
    try {
      await api.updateTestimonial(t.id, { is_published: !t.is_published });
      load();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div data-testid="manage-testimonials">
      <h2 className="font-serif text-2xl text-[#3E2723] mb-6">Manage Testimonials</h2>

      {/* Form */}
      <div className="border border-[#E6DCD3] rounded-xl p-6 mb-8 bg-[#FCFBF9]" data-testid="testimonial-form">
        <h3 className="text-sm font-medium text-[#1A1A1A] mb-4">{editing ? 'Edit Testimonial' : 'Add Testimonial'}</h3>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <input
            value={form.customer_name}
            onChange={e => setForm({...form, customer_name: e.target.value})}
            placeholder="Customer name"
            className="px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]"
            data-testid="testimonial-name-input"
          />
          <div className="flex items-center gap-4">
            <label className="text-sm text-[#786E66]">Rating:</label>
            <div className="flex gap-1">
              {[1,2,3,4,5].map(n => (
                <button key={n} onClick={() => setForm({...form, rating: n})} className="p-0.5">
                  <Star className={`h-5 w-5 ${n <= form.rating ? 'fill-[#C9A961] text-[#C9A961]' : 'text-gray-300'}`} />
                </button>
              ))}
            </div>
          </div>
        </div>
        <textarea
          value={form.content}
          onChange={e => setForm({...form, content: e.target.value})}
          placeholder="Testimonial content..."
          rows={3}
          className="w-full px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961] mb-4"
          data-testid="testimonial-content-input"
        />
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})} className="rounded" />
            Featured
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_published} onChange={e => setForm({...form, is_published: e.target.checked})} className="rounded" />
            Published
          </label>
          <div className="flex-1" />
          {editing && (
            <button onClick={resetForm} className="text-sm text-[#786E66] hover:text-[#1A1A1A]">Cancel</button>
          )}
          <button
            onClick={handleSave}
            className="bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full px-6 py-2 text-sm transition-all inline-flex items-center gap-2"
            data-testid="testimonial-save-btn"
          >
            <Plus className="h-4 w-4" /> {editing ? 'Update' : 'Add'}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-3" data-testid="testimonials-list">
        {testimonials.map((t, idx) => (
          <div key={t.id} className={`border rounded-xl p-5 flex flex-wrap items-start justify-between gap-4 ${t.is_published ? 'border-[#E6DCD3] bg-white' : 'border-gray-200 bg-gray-50 opacity-70'}`} data-testid={`testimonial-item-${idx}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <p className="font-medium text-[#1A1A1A] text-sm">{t.customer_name}</p>
                <div className="flex gap-0.5">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} className="h-3 w-3 fill-[#C9A961] text-[#C9A961]" />)}
                </div>
                {t.featured && <span className="text-xs bg-[#C9A961] text-white px-2 py-0.5 rounded-full">Featured</span>}
              </div>
              <p className="text-sm text-[#786E66] line-clamp-2">"{t.content}"</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => togglePublish(t)} className="p-2 hover:bg-[#F3EBE1] rounded-lg" title={t.is_published ? 'Unpublish' : 'Publish'}>
                {t.is_published ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-gray-400" />}
              </button>
              <button onClick={() => handleEdit(t)} className="p-2 hover:bg-[#F3EBE1] rounded-lg"><Pencil className="h-4 w-4 text-[#3E2723]" /></button>
              <button onClick={() => handleDelete(t.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="h-4 w-4 text-red-500" /></button>
            </div>
          </div>
        ))}
        {testimonials.length === 0 && <p className="text-center text-[#786E66] text-sm py-8">No testimonials yet. Add one above.</p>}
      </div>
    </div>
  );
};

export default ManageTestimonials;
